import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
// Material
import Typography from "@material-ui/core/Typography";
// Custom Hooks
import useDynamicHotelData from "../../../CustomHooks/useDynamicHotelData";
// Styles
import useStyles from "./SelectOrderType.style.js";
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import DeliveryTime from "../DeliveryTime/DeliveryTime.js";
import DateSlotPicker from "../../Checkout/DateSlotPicker/index.js";
import { useDispatch, useSelector } from "react-redux";
import { ORDER_TYPES } from "../../../utils/Constants.js";
import { generateDateSlots } from "../../../utils/utils.js";
import TimePicker from "../../Checkout/TimePicker/index.js";
import moment from "moment";
import { Info } from "@material-ui/icons";
import { clearTipAmount } from "../../../store/Cart/CartActions.js";
import { dispatchErrorMessageAsWarning } from "../../../utils/Shared";

const SelectOrderType = ({
  value = null,
  onSelect = () => {},
  formValues,
  handleClose,
  onDateAndTimeSelect,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { hotel, isTodayHoliday } = useSelector((state) => state.hotel);
  const [selectedOderType, setOrderType] = useState(value);
  const [dateSlots, setDateSlots] = useState([]);
  const [dateSlotsError, setDateSlotsError] = useState(false);
  const [date, setDate] = useState(null);
  const [typeOfOrder, setTypeOfOrder] = useState("SCHEDULED");
  const [time, setTime] = useState(null);
  const { checkoutDetails } = useSelector((state) => state.cart);
  const { theme } = useSelector((state) => state?.theme);
  const [orderTimeType, setOrderTimeType] = useState(null);
  const hasMounted = useRef(false);
  const {
    getOrderTypes,
    getDeliveryIntervalSlots,
    getTodayDeliveryIntervalSlots,
    isAsapAvailable,
    isMultipleOrderType,
    getAsapTime,
  } = useDynamicHotelData();
  const [newChange, setNewChange] = useState(false);
  const [noAvailableSlots, setNoAvailableSlots] = useState(false);
  // state which return is shedulued time have value or not

  const isOnlineOrderAvailable = useMemo(() => {
    return Boolean(hotel?.online_order_active === 1);
  }, [hotel?.online_order_active]);

  const isScheduledAvailable = useMemo(() => {
    const todayHasScheduled =
      Array.isArray(getTodayDeliveryIntervalSlots) &&
      getTodayDeliveryIntervalSlots.some(
        (slot) => slot.typeOfOrder === "SCHEDULED"
      );

    const futureHasScheduled =
      Array.isArray(getDeliveryIntervalSlots) &&
      getDeliveryIntervalSlots.some((slot) => slot.typeOfOrder === "SCHEDULED");

    const hasDateSlots = Array.isArray(dateSlots) && dateSlots.length > 0;

    return todayHasScheduled || (futureHasScheduled && hasDateSlots);
  }, [getTodayDeliveryIntervalSlots, getDeliveryIntervalSlots, dateSlots]);

  // Check if there are any scheduled slots available at all
  const hasAnyScheduledSlots = useMemo(() => {
    // Check if today has any SCHEDULED slots
    const todayScheduledSlots = getTodayDeliveryIntervalSlots?.filter(
      (slot) => slot.typeOfOrder === "SCHEDULED"
    ) || [];

    // Check if future days have any SCHEDULED slots
    const futureScheduledSlots = getDeliveryIntervalSlots?.filter(
      (slot) => slot.typeOfOrder === "SCHEDULED"
    ) || [];

    // Check if there are any date slots available
    const hasDateSlots = Array.isArray(dateSlots) && dateSlots.length > 0;



    // Return true only if we have actual scheduled time slots
    const result = todayScheduledSlots.length > 0 || futureScheduledSlots.length > 0;
    return result;
  }, [getTodayDeliveryIntervalSlots, getDeliveryIntervalSlots, dateSlots]);

  // Custom ASAP availability check - available until the restaurant closing time
  const isAsapAvailableUntilLastSlot = useMemo(() => {
    if (!hotel || !formValues.orderType) {
      return false;
    }

    const currentTime = moment();
    
    // Get today's working hours for the specific order type
    const dayInfo = hotel.workingInfo?.find(
      (elem) => elem.day === moment().day()
    );
    
    if (!dayInfo || !dayInfo.hotelDayInfo) {
      return false;
    }

    const hours = dayInfo.hotelDayInfo.find(
      (elem) => elem.type === formValues.orderType
    );

    if (!hours || !hours.running_hours || hours.running_hours.length === 0) {
      return false;
    }

    // Find the latest end time from all running hours
    const endTimes = hours.running_hours.map(elem => {
      const endTime = moment(elem.stop, "HH:mm");
      // If end time is before start time, it means it goes to next day
      const startTime = moment(elem.start, "HH:mm");
      if (endTime.isBefore(startTime)) {
        endTime.add(1, 'day');
      }
      return endTime;
    });
    
    const lastEndTime = moment.max(endTimes);
    
    if (!lastEndTime) {
      return false;
    }

    // Check if current time is before the restaurant closing time
    return currentTime.isBefore(lastEndTime) || currentTime.isSame(lastEndTime);
  }, [hotel, formValues.orderType]);

  // Check if both ASAP and SCHEDULED have no slots available
  const noBothAsapAndScheduledSlots = useMemo(() => {
    // If preorder is enabled with pre_min_day > 1, always allow SCHEDULED
    if (hotel?.is_preorder && hotel?.pre_min_day > 1) {
    
      return false;
    }
    
    // Check ASAP availability
    const asapNotAvailable = !isAsapAvailableUntilLastSlot || !hotel?.openingAvailable;
    
    // Check if there are accessible scheduled slots
    const todayScheduledSlots = getTodayDeliveryIntervalSlots?.filter(
      (slot) => slot.typeOfOrder === "SCHEDULED"
    ) || [];
 
    const futureScheduledSlots = getDeliveryIntervalSlots?.filter(
      (slot) => slot.typeOfOrder === "SCHEDULED"
    ) || [];

    // Enhanced logic for preorder scenarios
    let hasAccessibleScheduledSlots = false;
    
    // Check if today has scheduled slots
    if (todayScheduledSlots.length > 0) {
      hasAccessibleScheduledSlots = true;
    }
    // Standard logic for non-preorder scenarios
    else {
      hasAccessibleScheduledSlots = 
        todayScheduledSlots.length > 0 || 
        (futureScheduledSlots.length > 0 && dateSlots.some(slot => !moment(slot.date, "LL").isSame(moment(), 'date')));
    }
    
    const scheduledNotAvailable = !hasAccessibleScheduledSlots;
    

    
    return asapNotAvailable && scheduledNotAvailable;
  }, [isAsapAvailableUntilLastSlot, hotel?.openingAvailable, hotel?.is_preorder, hotel?.pre_min_day, getTodayDeliveryIntervalSlots, getDeliveryIntervalSlots, dateSlots]);

  // Filter dateSlots to remove today if there are no scheduled slots for today
  // Only apply this filter when user has selected SCHEDULED order type
  const datePickerSlots = useMemo(() => {
    // Only filter when SCHEDULED is selected
    if (orderTimeType !== "SCHEDULED") {
      return dateSlots; // Return all dates for non-scheduled modes
    }
    
    // Check if today has scheduled slots
    const todayScheduledSlots = getTodayDeliveryIntervalSlots?.filter(
      (slot) => slot.typeOfOrder === "SCHEDULED"
    ) || [];
    

    
    // If today has no scheduled slots, filter out today from dateSlots
    if (todayScheduledSlots.length === 0) {
      const filtered = dateSlots.filter(slot => {
        const isToday = moment(slot.date, "LL").isSame(moment(), 'date');
        return !isToday; // Keep all dates except today
      });
      

      
      return filtered;
    }
    
    // If today has scheduled slots, return all dateSlots

    return dateSlots;
  }, [orderTimeType, getTodayDeliveryIntervalSlots, dateSlots]);

  // function for ordertype selection
  const onOrderTypeSelect = (fieldName, type) => {
    setOrderType(type);
    onSelect(fieldName, type);
    dispatch(clearTipAmount());
  };

  // Select first order type if their is multiple ordertypes
  useEffect(() => {
    if (
      !formValues?.orderType &&
      getOrderTypes?.length > 0 &&
      !hotel?.order_later
    ) {
      onOrderTypeSelect("orderType", getOrderTypes[0].type);
    }
  }, [formValues?.orderType, getOrderTypes]);

  // switch to next orderType if, default selected ordertype has no asap and sheduled time

  useEffect(() => {
    if (formValues?.orderType && getOrderTypes?.length > 0) {
      const currentOrderType = formValues.orderType;
      const nextOrderType = getOrderTypes.find(
        (typeObj) => typeObj.type !== currentOrderType
      );

      if (
        !isAsapAvailableUntilLastSlot &&
        !isScheduledAvailable &&
        nextOrderType &&
        isTodayHoliday
      ) {
        const timeoutId = setTimeout(() => {
          onOrderTypeSelect("orderType", nextOrderType.type);
        }, 1000);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    formValues?.orderType,
    getOrderTypes,
    isAsapAvailableUntilLastSlot,
    isScheduledAvailable,
    isTodayHoliday,
  ]);


  const generateDatePickerSlots = () => {
    let orderTypeValue = value;
    if (value === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      orderTypeValue = ORDER_TYPES.OPENING;
    }
    const dates = generateDateSlots(hotel, orderTypeValue) || [];
    setDateSlots(dates);
    if (!dates || !dates?.length) {
      setDateSlotsError(true);
    }
  };

  useEffect(() => {
    const timeString = `${date}${time ? ` ${time}` : ""}`;
    const dTime = moment(moment(timeString)).toISOString();
    onDateAndTimeSelect(typeOfOrder || "SCHEDULED", dTime);
  }, [date, time, typeOfOrder]);

  useEffect(() => {
    if (hotel || formValues?.orderType || value) {
      generateDatePickerSlots();
    }
  }, [hotel, formValues?.orderType, value]);

  // Separate useEffect for initial order time type selection after all data is loaded
  useEffect(() => {
    // Only run if we haven't mounted yet and all required data is available
    if (
      !hasMounted.current && 
      formValues?.orderType && 
      getTodayDeliveryIntervalSlots && 
      getDeliveryIntervalSlots && 
      dateSlots?.length > 0 &&
      !newChange
    ) {
     
      
      // Initial render: Set default order time based on ASAP availability and order type
      const isAsapAvailable = 
        isAsapAvailableUntilLastSlot &&
        !isTodayHoliday &&
        !hotel?.order_later &&
        hotel?.openingAvailable &&
        (formValues?.orderType === "DELIVERY" || formValues?.orderType === "PICKUP");
      
      // Check if SCHEDULED is available (including preorder scenarios)
      const todayScheduledSlots = getTodayDeliveryIntervalSlots?.filter(
        (slot) => slot.typeOfOrder === "SCHEDULED"
      ) || [];
      
      const futureScheduledSlots = getDeliveryIntervalSlots?.filter(
        (slot) => slot.typeOfOrder === "SCHEDULED"
      ) || [];
      
      let isScheduledAvailable = false;
      
      // Check if today has scheduled slots
      if (todayScheduledSlots.length > 0) {
        isScheduledAvailable = true;
      }
      // Check if preorder is enabled and future dates are available
      else if (hotel?.is_preorder && hotel?.pre_min_day > 1) {
        const hasFutureDates = dateSlots.some(slot => !moment(slot.date, "LL").isSame(moment(), 'date'));
        isScheduledAvailable = futureScheduledSlots.length > 0 && hasFutureDates;
      }
      // Standard logic for non-preorder scenarios
      else {
        const hasFutureDates = dateSlots.some(slot => !moment(slot.date, "LL").isSame(moment(), 'date'));
        isScheduledAvailable = todayScheduledSlots.length > 0 || (futureScheduledSlots.length > 0 && hasFutureDates);
      }
      

      
      // Priority: ASAP if available, otherwise SCHEDULED if available
      const initialOrderTimeType = isAsapAvailable ? "ASAP" : "SCHEDULED";
      

      
      setOrderTimeType(initialOrderTimeType);
      handleOrderTime(initialOrderTimeType);
      hasMounted.current = true; // Mark initial render as done
    }
  }, [
    formValues?.orderType,
    getTodayDeliveryIntervalSlots,
    getDeliveryIntervalSlots,
    dateSlots,
    isAsapAvailableUntilLastSlot,
    isTodayHoliday,
    hotel?.order_later,
    hotel?.openingAvailable,
    hotel?.is_preorder,
    hotel?.pre_min_day,
    newChange
  ]);



  const onClick = () => {
    setNewChange(true);
  };

  const getAvailableTimeSlotsForDate = (selectedDate) => {
    // Get the time slots for today or another date based on the selected date
    const availableSlots = moment(selectedDate).isSame(moment(), "date")
      ? getTodayDeliveryIntervalSlots
      : getDeliveryIntervalSlots;

    // Add condition to filter for "SCHEDULED" time slots if orderTimeType is "SCHEDULED"
    if (orderTimeType === "SCHEDULED") {
      return availableSlots.filter((slot) => slot.typeOfOrder === "SCHEDULED");
    }

    return availableSlots; // Return all available slots if not filtered
  };

  const onSelectDataAndTime = ({ value, typeOfOrder } = {}, type) => {
    if (!value) return;
    if (typeOfOrder) {
      setTypeOfOrder(typeOfOrder);
    }
    if (type === "date") {
      // Set the selected date
      setDate(value);

      // Automatically select the first available time slot for the new date
      const availableTimeSlots = getAvailableTimeSlotsForDate(value);
      if (availableTimeSlots && availableTimeSlots.length > 0) {
        const firstTimeSlot = availableTimeSlots[0].value; // Assuming time slot object has a 'value' property
        setTime(firstTimeSlot); // Set the first available time
      }
    }

    if (type === "time") {
      setTime(value); // Set time manually if time is selected
    }
  };
  // Function to get the available time slots for a given date

  const getLabel = (arry) => {
    const asapSlot = arry.find((item) => item.typeOfOrder === "ASAP");
    return asapSlot ? asapSlot.label : undefined;
  };

  const filteredOrderTime = (array) => {
    const result = array.find((arr) => arr?.typeOfOrder === "ASAP");

    return result;
  };

  const filteredSheduledTimes = (array) => {
    const result = array.find((arr) => arr?.typeOfOrder === "SCHEDULED");

    return result;
  };

  const handleOrderTime = (e) => {
    setOrderTimeType(e);
    if (e === "ASAP") {
      const filteredTime = filteredOrderTime(
        getTodayDeliveryIntervalSlots?.length > 0
          ? getTodayDeliveryIntervalSlots
          : getDeliveryIntervalSlots
      );
      onSelectDataAndTime(filteredTime, "ASAP");
      onSelectDataAndTime(filteredTime, "time");
    } else if (e === "SCHEDULED") {
      // Check if there are any scheduled slots available at all
      const todayScheduledSlots = getTodayDeliveryIntervalSlots?.filter(
        slot => slot.typeOfOrder === "SCHEDULED"
      ) || [];
      
      const futureScheduledSlots = getDeliveryIntervalSlots?.filter(
        slot => slot.typeOfOrder === "SCHEDULED"
      ) || [];
      

      
      // Simplified logic since preorder is handled in noBothAsapAndScheduledSlots
      const hasAccessibleScheduledSlots = 
        todayScheduledSlots.length > 0 || 
        (futureScheduledSlots.length > 0 && dateSlots.some(slot => !moment(slot.date, "LL").isSame(moment(), 'date'))) ||
        (hotel?.is_preorder && hotel?.pre_min_day > 1); // Always allow for preorder scenarios
      
      // If no accessible scheduled slots at all, show warning
      if (!hasAccessibleScheduledSlots) {
        // Check if ASAP is available to switch back to
        const isAsapAvailable = 
          isAsapAvailableUntilLastSlot &&
          !isTodayHoliday &&
          !hotel?.order_later &&
          hotel?.openingAvailable &&
          isOnlineOrderAvailable;
        
        dispatchErrorMessageAsWarning({
          title: "Scheduled Orders Unavailable",
          message: "No time slots available for scheduled orders. Please try ASAP for immediate delivery or come back later.",
          noAutoClose: true,
        });
        
        // Automatically switch to ASAP if available
        if (isAsapAvailable) {
          setTimeout(() => {
            setOrderTimeType("ASAP");
            const filteredTime = filteredOrderTime(
              getTodayDeliveryIntervalSlots?.length > 0
                ? getTodayDeliveryIntervalSlots
                : getDeliveryIntervalSlots
            );
            onSelectDataAndTime(filteredTime, "ASAP");
            onSelectDataAndTime(filteredTime, "time");
          }, 100); // Small delay to ensure popup is shown first
        }
        
        return; // Don't proceed with normal logic
      }
      
    
      
      // Normal scheduled time selection logic
      const filteredTime = filteredSheduledTimes(
        getTodayDeliveryIntervalSlots?.length > 0 && !hotel?.order_later
          ? getTodayDeliveryIntervalSlots
          : getDeliveryIntervalSlots
      );
      onSelectDataAndTime(filteredTime, "SCHEDULED");
      onSelectDataAndTime(filteredTime, "time");
    }
  };

  const getDeliveryType = useMemo(() => {
    return selectedOderType;
  }, [selectedOderType]);

  const getLastTime = (array) => {
    if (!array || array?.length === 0) {
      return undefined;
    }


    const boundaryTime = moment("05:00 AM", "hh:mm A");
    const referenceTime = moment("11:58 PM", "hh:mm A");

    // Step 1: Check if any time slot is after 11:45 PM
    const hasAfter11_45PM = array.some((item) =>
      moment(item.value, "hh:mm A").isAfter(referenceTime)
    );

    // Step 2: If there's a time after 11:45 PM and hotel is allowing preorder, consider before 5:00 AM slots
    if (hasAfter11_45PM && hotel?.is_preorder > 0) {
      const before5am = array.filter((item) => {
        const itemTime = moment(item.value, "hh:mm A");
        return itemTime.hours() < 5;
      });

      if (before5am.length > 0) {
        const sortedBefore5am = [...before5am].sort(
          (a, b) => moment(a.value, "hh:mm A") - moment(b.value, "hh:mm A")
        );
        return sortedBefore5am[sortedBefore5am.length - 1]?.label;
      }
    }

    // Step 3: Fallback to return the last slot based on sorted time values
    const sortedArray = [...array].sort(
      (a, b) => moment(a.value, "hh:mm A") - moment(b.value, "hh:mm A")
    );

    return sortedArray[sortedArray.length - 1]?.label;
  };

  const convertToMoment = (time, dayIdx = 0) => {
    const baseDate = moment()
      .startOf("week")
      .add(dayIdx, "days")
      .format("YYYY-MM-DD");
    const localDate = moment.utc(baseDate + " " + time).local();

    const newTime = localDate.format("HH:mm");

    return moment(baseDate + " " + newTime);
  };

  const getNextAvailableTime = (openingHours) => {
    const now = moment();
    const todayIdx = now.day(); // 0‑6, Sun‑Sat

    /* 1️⃣  Holidays → quick look‑up set */
    const holidaySet = new Set(
      (hotel?.shop_hoilidays || []).map((h) =>
        moment(h.date).format("YYYY-MM-DD")
      )
    );

    /* 2️⃣  Scan up to seven calendar days ahead                          */
    for (let offset = 0; offset < 7; offset++) {
      const dateObj = now.clone().add(offset, "days").startOf("day");
      const dateKey = dateObj.format("YYYY-MM-DD");

      if (holidaySet.has(dateKey)) continue; // skip the holiday

      const dayIdx = (todayIdx + offset) % 7;
      const dayEntry = openingHours.find(
        (d) => d.day === dayIdx && !d.is_closed
      );
      if (!dayEntry) continue;

      const openInfo = dayEntry.hotelDayInfo?.find((i) => i.type === "OPENING");
      if (!openInfo || !openInfo.running_hours?.length) continue;

      /* sort slots chronologically by start time */
      const slots = [...openInfo.running_hours].sort((a, b) =>
        a.start.localeCompare(b.start)
      );

      for (const slot of slots) {
        const start = convertToMoment(slot.start, dayIdx);
        const stop = convertToMoment(slot.stop, dayIdx);
        const adjStop = stop.isSameOrBefore(start) ? stop.add(1, "day") : stop;

        if (adjStop.isSameOrBefore(now)) continue; // slot already ended

        /* Choose a pick time: immediately after ‘now’ if inside slot today,
         otherwise the slot’s start time                                         */
        let pick;
        if (offset === 0) {
          pick =
            start.isSameOrBefore(now) && adjStop.isAfter(now)
              ? now.clone().add(1, "minute")
              : start;
        } else {
          pick = start;
        }

        /* 3️⃣  Safety guard – malformed slot strings won’t crash us      */
        if (!moment.isMoment(pick)) continue;

        /* 4️⃣  Label: Today / Tomorrow / Weekday                         */
        const daysDiff = pick
          .clone()
          .startOf("day")
          .diff(now.clone().startOf("day"), "days");

        const label =
          daysDiff === 0
            ? "Today"
            : daysDiff === 1
            ? "Tomorrow"
            : pick.format("dddd");

        return `${label}, ${pick.format("hh:mm A")}`;
      }
    }

    return "No available slots";
  };
  



  return (
    <>
     
      {/* {isMultipleOrderType && ( */}
      <div className={classes.modalTitleSection}>
        <Typography
          className={classes.modalTitle}
          variant="h1"
          align="center"
          paragraph
        >
          {"Select Order Type"}
        </Typography>
        <Divider />
      </div>
      {/* )} */}
      {/* {isMultipleOrderType && ( */}
      <div id="checkout-orderTypeForm" className={clsx(classes.container)}>
        {getOrderTypes?.map((o, i) => (
          <div
            key={i}
            className={clsx(
              classes.btnNormal,
              o.type === selectedOderType && classes.item
              // o.active === false && classes.itemDisabled
            )}
            onClick={() => {
              onOrderTypeSelect("orderType", o.type);
              setNewChange(false);
            }}
          >
            <div className={classes.itemDiv}>
              <o.Icon
                className={clsx(
                  o.type === selectedOderType
                    ? classes.itemIcon
                    : classes.normalIcon
                )}
              />
            </div>
            <Typography
              // color="primary"
              className={clsx(
                o.type === selectedOderType
                  ? classes.itemText
                  : classes.normalText
              )}
              variant="caption"
            >
              {o.title}
            </Typography>
          </div>
        ))}
      </div>
      {/* )} */}
      {
        !noBothAsapAndScheduledSlots &&
         (
         <div className={classes.modalTitleSection} style={{ marginTop: "16px" }}>
            <Typography
              className={classes.modalTitle}
              variant="h1"
              align="center"
              paragraph
            >
              {"Order Time"}
            </Typography>
          </div>
        )
      }
     
      {noAvailableSlots ? (
        <div className={classes.noSlotDiv}>
          <Typography className={classes.noSlotTxt}>
          `You have arrived outside of our regular opening hours. However, you have the option to pre-order at <strong>${getNextAvailableTime(
            hotel?.workingInfo
          )}</strong>...`
          </Typography>
        </div>
      ) : noBothAsapAndScheduledSlots ? (
        <div className={classes.noSlotDiv}>
          <Typography className={classes.noSlotTxt}>
            No time slots available for both ASAP and scheduled orders. Please come back tomorrow for available options.
          </Typography>
        </div>
      ) : (
        <div className={classes.timeSelectDiv}>
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              value={orderTimeType}
              name="radio-buttons-group"
              onChange={(e) => handleOrderTime(e.target.value)}
              onClick={onClick}
            >
              <FormControlLabel
                value="ASAP"
                className={classes.radioLabel}
                control={
                  <Radio
                    className={classes.radioBtn}
                    classes={{ checked: classes.checked }}
                  />
                }
                label={
                  !isAsapAvailableUntilLastSlot ||
                  !hotel?.openingAvailable ||
                  hotel?.order_later
                    ? "ASAP : Unavailable"
                    : `ASAP :  ${getLabel(
                        getTodayDeliveryIntervalSlots.length > 0
                          ? getTodayDeliveryIntervalSlots
                          : getDeliveryIntervalSlots
                      ) || "Available"}`
                }
                checked={
                  orderTimeType === "ASAP" &&
                  !hotel?.order_later &&
                  isOnlineOrderAvailable &&
                  !isTodayHoliday
                }
                disabled={
                  !isAsapAvailableUntilLastSlot ||
                  hotel?.order_later ||
                  !isOnlineOrderAvailable ||
                  !hotel?.openingAvailable
                }
              />
              <FormControlLabel
                value="SCHEDULED"
                className={classes.radioLabel}
                disabled={!isOnlineOrderAvailable}
                control={
                  <Radio
                    className={classes.radioBtn}
                    classes={{ checked: classes.checked }}
                  />
                }
                label="Schedule for later"
                checked={orderTimeType === "SCHEDULED" || hotel?.order_later}
                // onChange={(e) => handleOrderTime(e.target.value)}
              />
            </RadioGroup>
          </FormControl>
          <div
            id="checkout-orderTypeForm"
            className={clsx(
              orderTimeType === "SCHEDULED"
                ? classes.sheduledContainer
                : classes.hideContainer
            )}
          >
            <DateSlotPicker
              slots={datePickerSlots}
              className={`w-100 ${classes.dateTimeSelection}`}
              onChange={onSelectDataAndTime}
            />
            <TimePicker
              slots={
                moment(formValues.deliveryTime).isSame(moment(), "date") &&
                // filteredTimeSlots(
                getTodayDeliveryIntervalSlots.length > 0
                  ? getTodayDeliveryIntervalSlots
                  : getDeliveryIntervalSlots
                // )
              }
              shouldFilter={true}
              isFromCheckout={false}
              date={date}
              onClick={onClick}
              orderTimeType={orderTimeType}
              className={`w-100 ${classes.dateTimeSelection}`}
              onChange={onSelectDataAndTime}
            />
          </div>

          {isOnlineOrderAvailable &&
            formValues?.deliveryType === "ASAP" &&
            getDeliveryType &&
            isAsapAvailableUntilLastSlot &&
            hotel?.openingAvailable && (
              <div className={classes.infoDiv}>
                <Info className={classes.infoIcon} />

                {isTodayHoliday ? (
                  /* 🟥 Holiday case */
                  <Typography className={classes.infoText}>
                    <span style={{ fontWeight: 800 }}>{getDeliveryType}</span>{" "}
                    is <u>not available today</u>
                  </Typography>
                ) : (
                  /* 🟩 Normal case */
                  !!getLastTime(
                    getTodayDeliveryIntervalSlots.length > 0
                      ? getTodayDeliveryIntervalSlots
                      : getDeliveryIntervalSlots
                  ) && (
                    <Typography className={classes.infoText}>
                      <span style={{ fontWeight: 800 }}>{getDeliveryType}</span>{" "}
                      available until{" "}
                      <span style={{ fontWeight: 800 }}>
                        {getLastTime(
                          getDeliveryIntervalSlots /* evening/future slots */
                        )}
                      </span>
                    </Typography>
                  )
                )}
              </div>
            )}
        
        </div>
      )}

      {/* {orderTimeType === "shedulued" && ( */}
      {/* )} */}
      <div className={classes.btnDiv}>
        <Button
          disabled={!formValues?.orderType || (noAvailableSlots && !(!isAsapAvailable && !isScheduledAvailable))}
          className={classes.continueBtn}
          onClick={handleClose}
        >
          Continue
        </Button>
      </div>
    </>
  );
};

export default SelectOrderType;
